import React, { useState, useEffect, useRef } from 'react';
import axios from '../../axios-config';
import { Store } from '../../models/store';
import { useStoreTypes } from '../../contexts/StoreTypesContext';

// Define days of week labels and initial state for operating days
const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const initialOperatingDays = daysOfWeek.map((_, index) => ({ dayOfWeek: index, isOpen: false }));

interface StoreFormProps {
    storeId?: number;
    onSuccess: () => void;
    onCancel: () => void;
}

const StoreForm: React.FC<StoreFormProps> = ({ storeId, onSuccess, onCancel }) => {
    const { storeTypes } = useStoreTypes();
    const [name, setName] = useState<string>('');
    const [slug, setSlug] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [pickupLocation, setPickupLocation] = useState<string>('');
    const [storeTypeId, setStoreTypeId] = useState<number>(1);
    const [parentStoreId, setParentStoreId] = useState<number | undefined>(undefined);
    const [organizationId, setOrganizationId] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    // Add operatingDays state
    const [operatingDays, setOperatingDays] = useState<{ dayOfWeek: number; isOpen: boolean }[]>(initialOperatingDays);
    const [openingTime, setOpeningTime] = useState<string>('07:30:00');
    const [closingTime, setClosingTime] = useState<string>('08:00:00');
    const didFetchStore = useRef(false);

    useEffect(() => {
        if (storeId && !didFetchStore.current) {
            didFetchStore.current = true;
            const fetchStore = async () => {
                setLoading(true);
                try {
                    const { data } = await axios.get(`/api/stores/${storeId}`, { withCredentials: true });
                    // Update state using data
                    setName(data.storeName);
                    setSlug(data.storeSlug);
                    setDescription(data.storeDescription || '');
                    setPickupLocation(data.pickupLocation || '');
                    setStoreTypeId(data.storeTypeId);
                    setParentStoreId(data.parentStoreId);
                    setOrganizationId(data.organizationId);
                    if (data.openingTime) setOpeningTime(data.openingTime);
                    if (data.closingTime) setClosingTime(data.closingTime);
                    if (data.storeOperatingDays) {
                        setOperatingDays(data.storeOperatingDays);
                    }
                } catch (err: any) {
                    setError(err.message);
                } finally {
                    setLoading(false);
                }
            };
            fetchStore();
        }
    }, [storeId]);

    const handleOperatingDayChange = (dayIndex: number) => {
        setOperatingDays(prev =>
            prev.map(day =>
                day.dayOfWeek === dayIndex ? { ...day, isOpen: !day.isOpen } : day
            )
        );
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        const payload: Omit<Store, 'id'> & { storeOperatingDays: { dayOfWeek: number; isOpen: boolean }[] } = {
            storeId,
            organizationId,
            storeName: name,
            storeSlug: slug,
            storeDescription: description || undefined,
            pickupLocation: pickupLocation || undefined,
            storeTypeId,
            parentStoreId,
            openingTime, // updated to state value
            closingTime, // updated to state value
            operatingDaysHours: '', // ...existing property...
            storeOperatingDays: operatingDays
        };

        try {
            let response;
            if (storeId) {
                response = await axios.put(`/api/stores/${storeId}`, payload, { withCredentials: true });
            } else {
                response = await axios.post('/api/stores', payload, { withCredentials: true });
            }
            console.log(response); //TOOD - display errors
            onSuccess();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!storeId) return;

        if (!window.confirm('Are you sure you want to delete this store?')) return;

        setIsDeleting(true);
        setError(null);

        try {
            await axios.delete(`/api/stores/${storeId}`, { withCredentials: true });
            onSuccess();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setIsDeleting(false);
        }
    };

    if (loading) {
        return <div className="text-center p-6">Loading...</div>;
    }

    return (
        <div className="p-6 bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-gray-200/50">
            <h2 className="text-2xl font-semibold mb-4">{storeId ? 'Edit Store' : 'Create New Store'}</h2>
            {error && <div className="mb-4 text-red-500">{error}</div>}
            <form onSubmit={handleSubmit}>
                <div className="mb-4">
                    <label htmlFor="store-name" className="block mb-1 font-medium">Name:</label>
                    <input
                        id="store-name"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:border-indigo-500"
                    />
                </div>
                <div className="mb-4">
                    <label htmlFor="store-slug" className="block mb-1 font-medium">Slug:</label>
                    <input
                        id="store-slug"
                        type="text"
                        value={slug}
                        onChange={(e) => setSlug(e.target.value)}
                        required
                        className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:border-indigo-500"
                    />
                </div>
                <div className="mb-4">
                    <label htmlFor="store-description" className="block mb-1 font-medium">Description:</label>
                    <textarea
                        id="store-description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:border-indigo-500"
                    />
                </div>
                <div className="mb-4">
                    <label htmlFor="store-pickup-location" className="block mb-1 font-medium">Pickup Location:</label>
                    <input
                        id="store-pickup-location"
                        type="text"
                        value={pickupLocation}
                        onChange={(e) => setPickupLocation(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:border-indigo-500"
                    />
                </div>
                <div className="mb-4">
                    <label htmlFor="store-type-id" className="block mb-1 font-medium">Store Type:</label>
                    <select
                        id="store-type-id"
                        value={storeTypeId}
                        onChange={(e) => setStoreTypeId(Number(e.target.value))}
                        required
                        className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:border-indigo-500"
                    >
                        {storeTypes && storeTypes.map(type => (
                            <option key={type.storeTypeId} value={type.storeTypeId}>{type.storeTypeName}</option>
                        ))}
                    </select>
                </div>
                <div className="mb-4">
                    <label htmlFor="parent-store-id" className="block mb-1 font-medium">Parent Store ID:</label>
                    <input
                        id="parent-store-id"
                        type="number"
                        value={parentStoreId || ''}
                        onChange={(e) => {
                            const value = e.target.value;
                            setParentStoreId(value ? Number(value) : undefined);
                        }}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:border-indigo-500"
                    />
                </div>
                {/* New Opening and Closing Time Fields */}
                <div className="mb-4 grid grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="opening-time" className="block mb-1 font-medium">Opening Time:</label>
                        <input
                            id="opening-time"
                            type="time"
                            value={openingTime}
                            onChange={(e) => setOpeningTime(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:border-indigo-500"
                        />
                    </div>
                    <div>
                        <label htmlFor="closing-time" className="block mb-1 font-medium">Closing Time:</label>
                        <input
                            id="closing-time"
                            type="time"
                            value={closingTime}
                            onChange={(e) => setClosingTime(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:border-indigo-500"
                        />
                    </div>
                </div>
                {/* New Operating Days checkboxes */}
                <div className="mb-4">
                    <span className="block mb-1 font-medium">Operating Days:</span>
                    <div className="flex flex-wrap gap-4">
                        {operatingDays.map(day => (
                            <label key={day.dayOfWeek} className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    checked={day.isOpen}
                                    onChange={() => handleOperatingDayChange(day.dayOfWeek)}
                                    className="form-checkbox h-5 w-5 text-indigo-600"
                                />
                                <span>{daysOfWeek[day.dayOfWeek]}</span>
                            </label>
                        ))}
                    </div>
                </div>
                <div className="flex space-x-3">
                    <button type="submit" disabled={loading} className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600">
                        {storeId ? 'Update' : 'Create'}
                    </button>
                    {storeId && (
                        <button
                            type="button"
                            onClick={handleDelete}
                            disabled={isDeleting}
                            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                        >
                            {isDeleting ? 'Deleting...' : 'Delete'}
                        </button>
                    )}
                    <button type="button" onClick={onCancel} disabled={loading || isDeleting} className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600">
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

export default StoreForm;
